import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import Seo from "@/components/Seo";
import { CtaSection } from "@/components/CtaSection";

const FAQS = [
  {
    q: "Who is Covelli for?",
    a: "Ambitious brands — ecommerce, SaaS, and local services — that are ready to invest in real growth. If you want a partner obsessed with revenue instead of likes, we'll get along.",
  },
  {
    q: "What results can I expect?",
    a: "Every engagement starts with KPIs we define together — ROAS, cost per lead, booked calls, revenue. We optimize for those numbers and report on them plainly. No vanity metrics.",
  },
  {
    q: "How long until we see results?",
    a: "Most brands see early signal within 30 days and meaningful traction in 60–90 days. Paid ads move fastest; systems and content compound over time.",
  },
  {
    q: "What's the minimum budget?",
    a: "Our Launch package starts at $1,799/month CAD. On top of that, we recommend an ad spend budget you're comfortable testing with — we'll tell you honestly what's realistic for your market.",
  },
  {
    q: "Do you work with startups / small businesses?",
    a: "Yes. Launch was built specifically for early-stage brands and local businesses that need senior-level marketing without an in-house hire.",
  },
  {
    q: "How do we work together?",
    a: "We start with a strategy call, then an audit and roadmap. From there it's a build–launch–optimize rhythm with regular calls and transparent reporting. You always know what we're doing and why.",
  },
  {
    q: "Can you just run ads without strategy?",
    a: "No — ads without strategy burn budget. Every engagement includes a strategic foundation, even if it's lightweight. It's how we protect your results (and our motto).",
  },
  {
    q: "Do you offer one-off projects?",
    a: "Yes. One-off audits, funnel builds and creative sprints are available through a custom quote — tell us what you need on the contact page.",
  },
];

const Item = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div data-testid={`faq-item-${index}`} className="border-b border-slate-200">
      <button
        data-testid={`faq-question-${index}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-display text-lg font-bold text-slate-900 sm:text-xl">{q}</span>
        <Plus
          size={18}
          className={`shrink-0 transition-transform duration-300 ${open ? "rotate-45 text-kinetic" : "text-slate-400"}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p data-testid={`faq-answer-${index}`} className="max-w-3xl pb-7 text-base leading-relaxed text-slate-600">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Faq() {
  return (
    <>
      <Seo
        title="FAQ — Covelli"
        siteName="Covelli"
        description="Answers to common questions about working with Covelli: results, timelines, budgets and process."
      />
      <section data-testid="faq-hero" className="mx-auto max-w-7xl px-6 pb-6 pt-32 md:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-skybrand"
        >
          FAQ
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl md:text-6xl"
        >
          Frequently Asked Questions
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600"
        >
          If you don't see your question here, just ask.
        </motion.p>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 pt-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="border-t border-slate-200"
        >
          {FAQS.map((f, i) => (
            <Item key={f.q} q={f.q} a={f.a} index={i} />
          ))}
        </motion.div>
      </section>

      <CtaSection
        testid="faq-cta"
        headline="Still not sure? Let's talk."
        subtext="No pressure. Just a clear plan and honest answers."
        buttonText="Book a Strategy Call"
      />
    </>
  );
}
